import * as RadixSwitch from '@radix-ui/react-switch'
import React, { ReactNode, useId } from 'react'
import { cn } from './cn'

export interface SwitchProps {
  checked?: boolean
  defaultChecked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  label?: ReactNode
  description?: ReactNode
  id?: string
  className?: string
  'aria-label'?: string
}

export const Switch = ({ label, description, id, className, ...props }: SwitchProps) => {
  const autoId = useId()
  const switchId = id ?? `switch-${autoId.replace(/:/g, '')}`
  const control = (
    <RadixSwitch.Root
      id={switchId}
      className={cn(
        'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-pill border border-solid border-hairline bg-surface-2 p-0.5 transition-colors duration-200',
        'data-[state=checked]:border-transparent data-[state=checked]:bg-accent',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        'disabled:cursor-not-allowed disabled:opacity-40',
        !label && className
      )}
      {...props}
    >
      <RadixSwitch.Thumb
        className={cn(
          'block size-[22px] rounded-full bg-fg shadow-sm transition-transform duration-200 ease-[var(--plug-ease-spring)]',
          'data-[state=checked]:translate-x-5 data-[state=checked]:bg-on-accent motion-reduce:transition-none'
        )}
      />
    </RadixSwitch.Root>
  )
  if (!label) return control
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <label htmlFor={switchId} className="flex min-w-0 cursor-pointer flex-col gap-0.5">
        <span className="text-[15px] leading-[1.3] font-semibold text-fg">{label}</span>
        {description && <span className="text-[13px] leading-[1.4] text-fg-2">{description}</span>}
      </label>
      {control}
    </div>
  )
}
